import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';
import * as Select from '@radix-ui/react-select';
import { SnapshotType } from '@upright/common/model/versions/types';
import { compareVersions } from '@upright/common/utils';
import { useGetAvailableVersions, useGetVersions } from '@upright/ui/api/model/versions';
import CheckIcon from '@upright/ui/common/generic/components/icons/hero-icons/hi-check.svg';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsTeam, useViewSetting } from '@upright/ui/common/generic/hooks';
import { setModelVersionAndRelease } from '@upright/ui/untidy/redux/actions/actions';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { navAnimateScaleIn, navAnimateScaleOut, navBorderStyles, navFastTransition, navItemStyles } from '../shared';
import { VersionWarning } from './version-warning';

export const HeaderVersionSelector = () => {
  const dispatch = useDispatch();
  const isTeam = useIsTeam();
  const isExpertMode = useViewSetting('internalAdmin.headerVersionSelector');
  const { version } = useSelector(getVersionAndRelease);
  const { error, isLoading, data: values } = useGetSelectorValues();
  const { data: availableVersions } = useGetAvailableVersions(resp => resp.map(r => r.number));

  if (!isTeam || !isExpertMode) {
    return <></>;
  }

  if (error || isLoading || !values || !availableVersions) {
    return <CurrentSelection />;
  }

  if (values.length === 1) {
    return <CurrentSelection />;
  }

  return (
    <Root>
      <>
        <VersionWarning selectedVersionNumber={version} />
        <Select.Root
          onValueChange={(value: string) => {
            if (availableVersions.includes(value)) {
              dispatch(setModelVersionAndRelease(value, null));
            }
          }}
          value={availableVersions.includes(version) ? version : 'Unavailable'}
        >
          <StyledTrigger>
            <span>Product data:</span>
            <Select.Value placeholder={'foobar'} />
            <Select.Icon asChild>
              <>
                <ChevronDown16 />
              </>
            </Select.Icon>
          </StyledTrigger>
          <StyledContent>
            {version !== values[0] && (
              <StyledScrollUpButton>
                <ChevronUp16 />
              </StyledScrollUpButton>
            )}
            <StyledViewPort>
              <Select.Group>
                {values.map(value => {
                  return (
                    <StyledItem key={value} value={value}>
                      <StyledItemIndicator>
                        <CheckIcon />
                      </StyledItemIndicator>
                      <Select.ItemText>{value}</Select.ItemText>
                    </StyledItem>
                  );
                })}
              </Select.Group>
            </StyledViewPort>
            <StyledScrollDownButton>
              <ChevronDown16 />
            </StyledScrollDownButton>
          </StyledContent>
        </Select.Root>
      </>
    </Root>
  );
};

function CurrentSelection() {
  const { release, version } = useSelector(getVersionAndRelease);
  return release ? (
    <Root>
      <span>Release: {release}</span>
    </Root>
  ) : (
    <Root>
      <span>Version: {version}</span>
    </Root>
  );
}

function useGetSelectorValues() {
  const { version } = useSelector(getVersionAndRelease);
  return useGetVersions(versions => {
    const currentVersion = versions.find(v => v.number === version);
    const currentVersionIsDeleted = currentVersion?.type === SnapshotType.DELETED;
    const availableVersions = versions.filter(v => v.type !== SnapshotType.DELETED).map(v => v.number);

    const selectorVersions = currentVersionIsDeleted ? [...availableVersions, 'Unavailable'] : availableVersions;
    return selectorVersions.sort((a, b) => compareVersions(b, a));
  });
}

const Root = styled.div`
  display: flex;
  align-items: center;
  > span {
    color: ${BouColor.WHITE};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
  &:hover,
  &:active {
    > span {
      color: ${BouColor.WHITE};
    }
  }
`;

const StyledTrigger = styled(Select.Trigger)`
  ${navItemStyles};
  ${getTextStyle({ size: 'xxxs' })}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  &:active {
    > span,
    svg {
      color: ${BouColor.WHITE};
    }
  }
  &[data-state='open'] {
    > svg {
      transition: transform ${navFastTransition};
      transform: rotate(180deg);
    }
  }
`;

const StyledContent = styled(Select.Content)`
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  ${navBorderStyles}
  position: absolute; // Needed to render the content below the trigger and not on top of it. Radix will provide custom options for this later.
  right: 0;
  top: 30px;
  &[data-state='open'] {
    ${navAnimateScaleIn}
  }
  &[data-state='closed'] {
    ${navAnimateScaleOut}
  }
`;

const StyledViewPort = styled(Select.Viewport)`
  min-width: 160px;
  max-height: 200px;
`;

const StyledItem = styled(Select.Item)`
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${BouColor.PLATFORM_TEXT_WHITE};
  display: flex;
  align-items: center;
  height: 28px;
  padding-left: 25px;
  outline: none;
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  &[data-disabled] {
    color: ${BouColor.PLATFORM_DISABLED_GRAY};
    cursor: none;
  }

  &[data-highlighted] {
    background-color: ${BouColor.PLATFORM_BG_GRAY};
    * {
      color: ${BouColor.PLATFORM_TEXT_WHITE};
    }
  }
`;

const StyledItemIndicator = styled(Select.ItemIndicator)`
  position: absolute;
  left: 0;
  width: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 14px;
  }
`;

const scrollButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 25px;
  background-color: ${BouColor.WHITE};
  border-radius: 6px;
  color: ${BouColor.BLACK};
  cursor: default;
`;

const StyledScrollUpButton = styled(Select.ScrollUpButton)`
  ${scrollButtonStyles};
`;
const StyledScrollDownButton = styled(Select.ScrollDownButton)`
  ${scrollButtonStyles};
`;
